import { useEffect, useState, useCallback } from "react";
import {
  isFuture,
  isPast,
  isWithinInterval,
  subDays,
  addHours,
} from "date-fns";
import { v4 as uuidv4 } from "uuid";

import { useFirestore } from "./useFirestore";
import { useUser } from "./useUser";
import { useSnackbar } from "./useSnackbar";

export const useLessons = (seasonId, opts = {}) => {
  const { showAll, autoLoad = true } = opts;
  const { enqueueSnackbar } = useSnackbar();
  const {
    getDocs,
    orderBy,
    waitForDocUpdate,
    setDoc,
    deleteDoc,
    where,
    getCollectionGroup,
  } = useFirestore();
  const { user, updateProfile } = useUser();
  const [lessons, setLessons] = useState([]);
  const [isReady, setIsReady] = useState(false);
  const [isBusySet, setIsBusySet] = useState(new Set([]));

  const setIsBusy = (lessonId, isBusy) => {
    setIsBusySet((set) => {
      if (isBusy) {
        set.add(lessonId);
      } else {
        set.delete(lessonId);
      }
      return new Set([...set]);
    });
  };

  const isBusy = (lessonId) => isBusySet.has(lessonId);

  const getScore = (lessonId) => {
    const score = user?.profile?.quizScores?.[lessonId]?.score;
    if (new Set([null, undefined]).has(score)) {
      return null;
    }
    return score;
  };

  const processLessons = (lessons) => {
    return lessons.map((lesson) => {
      const now = new Date();
      const date = lesson.date.toDate();
      return {
        ...lesson,
        date,
        canChangeApplication: isFuture(date),
        isPastLesson: isPast(addHours(date, 8)),
        isCurrentLesson: isWithinInterval(now, {
          start: date,
          end: addHours(date, 8),
        }),
        score: getScore(lesson.id),
      };
    });
  };

  const loadLessons = useCallback(async (seasonId, { showAll }) => {
    setIsReady(false);
    try {
      const lessons = (
        await getDocs(
          `seasons/${seasonId}/lessons`,
          orderBy("date"),
          ...(showAll ? [] : [where("isPublic", "==", true)])
        )
      ).docs;
      setLessons(
        processLessons(lessons.map((doc) => ({ id: doc.id, ...doc.data() })))
      );
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Kaut kas nogāja greizi! Lūdzu mēģini vēlāk.", {
        variant: "error",
      });
    }
    setIsReady(true);
  }, []);

  const applyForLesson = async (lessonId) => {
    const { profile } = user;
    setIsBusy(lessonId, true);
    try {
      await updateProfile(user.email, {
        ...profile,
        lessonApplications: [
          ...new Set([...(profile.lessonApplications || []), lessonId]),
        ],
      });
      enqueueSnackbar("Dalība veiksmīgi pieteikta!", { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Kaut kas nogāja greizi! Lūdzu mēģini vēlāk.", {
        variant: "error",
      });
    }
    setIsBusy(lessonId, false);
  };

  const unapplyForLesson = async (lessonId) => {
    const { profile } = user;
    const lessonApplications = new Set(profile.lessonApplications || []);
    lessonApplications.delete(lessonId);
    setIsBusy(lessonId, true);
    try {
      await updateProfile(user.email, {
        ...profile,
        lessonApplications: [...lessonApplications],
      });
      enqueueSnackbar("Dalība veiksmīgi atteikta!", { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Kaut kas nogāja greizi! Lūdzu mēģini vēlāk.", {
        variant: "error",
      });
    }
    setIsBusy(lessonId, false);
  };

  const registerAttendance = async (code) => {
    const { profile } = user;

    try {
      const [lessonDoc] = (
        await getCollectionGroup(`lessons`, where("joinCode", "==", code))
      ).docs;
      if (!lessonDoc) {
        enqueueSnackbar("Nodarbība netika atrasta!", {
          variant: "error",
        });
        return false;
      }
      const lessonId = lessonDoc.id;
      await updateProfile(user.email, {
        ...profile,
        lessonAttendance: [
          ...new Set([...(profile.lessonAttendance || []), lessonId]),
        ],
      });
      enqueueSnackbar("Dalība veiksmīgi reģistrēta!", { variant: "success" });
      return true;
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Kaut kas nogāja greizi! Lūdzu mēģini vēlāk.", {
        variant: "error",
      });
      return false;
    }
  };

  const hasAppliedForLesson = (lessonId) => {
    const lessonApplications = user?.profile?.lessonApplications || [];
    return lessonApplications.includes(lessonId);
  };

  const waitForScore = async (lessonId) => {
    setIsBusy(lessonId, true);
    try {
      await waitForDocUpdate("users", user.email, (snapshot, resolve) => {
        const lesson = snapshot.data();
        const { quizSubmissions, quizScores } = lesson;
        if (new Set(quizSubmissions).has(lessonId) && quizScores[lessonId]) {
          setLessons((lessons) =>
            lessons.map((lesson) => {
              if (lesson.id !== lessonId) {
                return lesson;
              }
              return { ...lesson, score: quizScores[lessonId].score };
            })
          );
          resolve();
        }
      });
      enqueueSnackbar("Rezultāts saņemts!", {
        variant: "success",
      });
    } catch (e) {
      console.error(e);
      enqueueSnackbar("Kaut kas nogāja greizi! Nevarējām sagaidīt rezultātu.", {
        variant: "error",
      });
    }
    setIsBusy(lessonId, false);
  };

  const saveLesson = async (id, newLesson) => {
    setIsBusy(id, true);
    const lessonsWithSameId = lessons.filter((lesson) => lesson.id === id);
    if (lessonsWithSameId.length !== 1) {
      enqueueSnackbar("Nodarbību ID jābūt unikāliem!", {
        variant: "error",
      });
      setIsBusy(id, false);
      return;
    }
    try {
      await setDoc(`seasons/${seasonId}/lessons`, id, newLesson, {
        merge: true,
      });
      enqueueSnackbar("Nodarbība saglabāta!", {
        variant: "success",
      });
      setLessons((lessons) =>
        lessons.map((lesson) =>
          lesson.id === id ? { ...newLesson, id } : lesson
        )
      );
    } catch (e) {
      console.error(e);
      enqueueSnackbar("Kaut kas nogāja greizi!", {
        variant: "error",
      });
    }
    setIsBusy(id, false);
  };

  const deleteLesson = async (id) => {
    setIsBusy(id, true);
    const lesson = lessons.find((lesson) => lesson.id === id);
    if (!lesson.unsaved) {
      try {
        await deleteDoc(`seasons/${seasonId}/lessons`, id);
        enqueueSnackbar("Nodarbība dzēsta!", {
          variant: "success",
        });
      } catch (e) {
        console.error(e);
        enqueueSnackbar("Kaut kas nogāja greizi!", {
          variant: "error",
        });
      }
    }
    setLessons((lessons) => lessons.filter((lesson) => lesson.id !== id));
    setIsBusy(id, false);
  };

  const addLesson = () => {
    setLessons((lessons) => [
      ...lessons,
      {
        id: `${seasonId}_${lessons.length + 1}`,
        joinCode: uuidv4(),
        name: "Jauna nodarbība",
        date: new Date(),
        isPublic: false,
        includedInSC: true,
        applicationsOpen: false,
        questionnaireUrl: "",
        questionnaireOpen: false,
        quizUrl: "",
        quizOpen: false,
        unsaved: true,
      },
    ]);
  };

  useEffect(() => {
    if (seasonId && autoLoad) {
      loadLessons(seasonId, { showAll });
    }
  }, [seasonId, loadLessons]);

  return {
    loadLessons,
    lessons,
    isBusy,
    isReady,
    applyForLesson,
    unapplyForLesson,
    hasAppliedForLesson,
    waitForScore,
    saveLesson,
    deleteLesson,
    addLesson,
    registerAttendance,
  };
};
